import { Coins } from 'lucide-react';

import { getModelCostInfo, type ModelPricingInput } from '@/lib/model-cost';
import { cn } from '@/lib/utils';

interface ModelCostLabelProps {
  /** A model row's `pricing` (from `/v1/models` or `/v1/models/definitions`). */
  pricing: ModelPricingInput;
  className?: string;
  /** Drop the coin icon for dense rows where the icon would crowd the layout. */
  iconless?: boolean;
}

/**
 * Renders a model's user-facing (marked-up) cost as a compact muted label with a
 * hover tooltip carrying the full breakdown. Returns `null` for free / unpriced
 * models, so it is safe to drop into any model selector unconditionally.
 *
 * Mirrors the plugin-builder header's credit treatment (Coins icon + credits),
 * but shows a per-model RATE rather than an accumulated total.
 */
export function ModelCostLabel({ pricing, className, iconless }: ModelCostLabelProps) {
  const info = getModelCostInfo(pricing);
  if (!info) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[10px] leading-tight text-muted-foreground',
        className,
      )}
      title={info.detail}
    >
      {!iconless && <Coins className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span>{info.compact}</span>
    </span>
  );
}
